import {config} from '@/config';
import type {VendorRegistration} from '@/lib/registration-schema';
import {toApiPayload} from '@/lib/registration-payload';

export class RegistrationError extends Error {}

/** 400 con errores por campo (RFC 9457). Se pintan bajo cada input. */
export class ValidationFailure extends RegistrationError {
  constructor(public fields: {field: string; message: string}[]) {
    super('Validation failed');
  }
}

/** 429: rate limit de 10 req/s por IP (burst 20). */
export class RateLimitError extends RegistrationError {}

interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errors?: {field: string; message: string}[];
}

/**
 * Envía la solicitud de vendor a POST /api/application (ver openapi.yaml).
 *
 * En modo "mock" simula la latencia y responde éxito, para probar el flujo sin
 * la API del cliente. Desde el navegador se pueden forzar los caminos de error:
 *
 *   sessionStorage.setItem("nm-mock-fail", "1")  → error genérico (500 / red)
 *   sessionStorage.setItem("nm-mock-429", "1")   → rate limit
 *   sessionStorage.setItem("nm-mock-400", "1")   → errores de validación por campo
 *
 * `idempotencyKey` debe ser un UUID v4 nuevo por solicitud, y el MISMO en los
 * reintentos del usuario: es lo que evita registros duplicados.
 */
export async function submitRegistration(data: VendorRegistration, idempotencyKey: string): Promise<void> {
  if (config.API_MODE === 'mock') {
    await new Promise((r) => setTimeout(r, 1400));
    if (takeMockFlag('nm-mock-429')) throw new RateLimitError('mock rate limit');
    if (takeMockFlag('nm-mock-400')) {
      throw new ValidationFailure([
        {field: 'email', message: 'The value is not a valid email address.'},
        {field: 'products_services', message: 'This field is required.'},
      ]);
    }
    if (takeMockFlag('nm-mock-fail')) throw new RegistrationError('mock failure');
    return;
  }

  let res: Response;
  try {
    res = await fetch(config.API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify(toApiPayload(data)),
      signal: AbortSignal.timeout(20_000),
    });
  } catch {
    // Fallo de red, CORS bloqueado o timeout: no hay respuesta que leer.
    throw new RegistrationError('Network error');
  }

  // 409 = la solicitud ya se guardó con esta misma clave (doble click o
  // reintento). El spec es explícito: el frontend debe tratarlo como éxito.
  if (res.ok || res.status === 409) return;

  if (res.status === 429) {
    throw new RateLimitError('Rate limit exceeded');
  }

  if (res.status === 400) {
    const problem = await readProblem(res);
    if (problem?.errors?.length) throw new ValidationFailure(problem.errors);
    // Sin errors[]: header ausente o inválido, es un bug nuestro.
    throw new RegistrationError(problem?.detail ?? 'Bad request');
  }

  throw new RegistrationError(`Registration failed: ${res.status}`);
}

async function readProblem(res: Response): Promise<ProblemDetail | null> {
  try {
    return (await res.json()) as ProblemDetail;
  } catch {
    return null;
  }
}

function takeMockFlag(key: string): boolean {
  if (!sessionStorage.getItem(key)) return false;
  sessionStorage.removeItem(key);
  return true;
}
